import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useAuth } from '../../hooks';

export interface IGeoDataResponseRaw {
	countries: {
		code: string;
		name: string;
	}[];
	currencies: {
		default: string;
		currencies: {
			iso: string;
			symbol: string;
			name: string;
			decimalCount: number;
		}[];
	};
}

export interface IGeoDataResponse {
	countries: {
		value: string;
		label: string;
	}[];
	currencies: {
		value: string;
		symbol: string;
		label: string;
		decimalCount: number;
	}[];
}

export interface IServiceStatusResponse {
	serviceLevel: 'SWIFT' | 'SEPA';
	status: 'OPENED' | 'CLOSED' | null;
}

const useGeoData = () => {
	const { session } = useAuth();

	const getCountriesAndCurrencies = async (): Promise<IGeoDataResponseRaw> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/utilities/geo-data`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['geo-data'],
		queryFn: () => getCountriesAndCurrencies(),
		select: (data): IGeoDataResponse => {
			const countries = data?.countries?.map((country) => ({
				value: country.code,
				label: country.name,
			}));

			const currencies = data?.currencies?.currencies?.map((curr) => ({
				value: curr.iso,
				label: curr.name,
				symbol: curr.symbol,
				decimalCount: curr.decimalCount,
			}));

			return {
				currencies,
				countries,
			};
		},
		enabled: !!session?.tokenized,
	});
};

const useUserServiceStatus = (service?: 'swift' | 'sepa') => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const getServiceStatus = async (): Promise<IServiceStatusResponse> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/service-status/${service}`,
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['service-status', service, selectedProfile],
		queryFn: () => getServiceStatus(),
		enabled: !!service && !!session?.tokenized && !!selectedProfile?.id,
	});
};

export { useGeoData, useUserServiceStatus };
